import * as React from 'react';
import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { pipelineInProgress } from '@utils/pipelines';
import {
  ACTIVATE_PIPELINE_BUTTON_LABEL,
  CANCEL_BUTTON_LABEL,
  CREATED_BY_LABEL,
  DATE_LABEL,
  DEBUG_BUTTON_LABEL,
  DECLINE_BUTTON_LABEL,
  DELETE_BUTTON_LABEL,
  DEPLOY_BUTTON_LABEL,
  DETAILS_BUTTON_LABEL,
  DUPLICATE_BUTTON_LABEL,
  EDIT_BUTTON_LABEL,
  EXPORT_PIPELINE_FEEDBACK_CSV_BUTTON_LABEL,
  MenuActionsPipelinesList,
  NAME_LABEL,
  OBSERVABILITY_BUTTON_LABEL,
  PIPELINE_SERVICE_LEVELS_LABELS,
  PipelineDesiredStatusCodes,
  PipelineOutputType,
  PipelineStatusCodes,
  PROMPT_EXPLORER_BUTTON_LABEL,
  RENAME_BUTTON_LABEL,
  SERVICE_LEVEL_LABEL,
  SHARE_BUTTON_LABEL,
  STATUS_LABEL,
  TRY_AGAIN_BUTTON_LABEL,
  UNDEPLOY_BUTTON_LABEL,
} from '@constants/pipelines';
import {
  IPipeline,
  PermissionAction,
  PermissionAsset,
  PipelineServiceLevel,
  WithPermissions,
} from '@redux/types/types';
import UserAvatar from '@components/common/userAvatar/userAvatar';
import WorkspaceLink from '@components/common/WorkspaceLink/WorkspaceLink';
import DataTableActions from '@components/dataTable/DataTableActions';
import PipelineServiceLevelBadge from '@components/pipelines/pipelineServiceLevelBadge/PipelineServiceLevelBadge';
import PipelineStatusTag from '@components/pipelineStatusTag/PipelineStatusTag';
import { LimitCategory } from '@modules/Upgrade/consts/pricing';
import useFreeLimits from '@modules/Upgrade/hooks/useFreeLimits';
import styles from '../features/pipelinesList/pipelinesList.module.scss';

interface Props {
  isPipelineUnhealthy: (_: IPipeline) => boolean;
  getUnhealthyMessage: (pipeline: IPipeline) => string | JSX.Element;
  deployPipeline: (_: string) => Promise<void>;
  activatePipeline: (pipelineName: string) => Promise<void>;
  setPipelineToShare: React.Dispatch<React.SetStateAction<string>>;
  onUndeployPipeline: (pipeline: IPipeline) => Promise<void>;
  handleMoreActionClick: (key: string, pipelineName: string, pipeline: IPipeline) => Promise<void>;
  deletePipeline: (pipelineName: string) => Promise<void>;
  setPipelineToDelete: React.Dispatch<React.SetStateAction<IPipeline | null>>;
}

export const useColumns = ({
  isPipelineUnhealthy,
  getUnhealthyMessage,
  deployPipeline,
  activatePipeline,
  setPipelineToShare,
  onUndeployPipeline,
  handleMoreActionClick,
  deletePipeline,
  setPipelineToDelete,
}: Props) => {
  const { isLimitedUser, isLimitExceed } = useFreeLimits();
  const writePipelinePermission = { [PermissionAsset.PIPELINES]: PermissionAction.WRITE };
  const readPipelinePermission = { [PermissionAsset.PIPELINES]: PermissionAction.READ };

  const writeSharedPrototypePermission = {
    [PermissionAsset.SHARED_PROTOTYPES]: PermissionAction.WRITE,
  };
  const readFeedbackPermission = { [PermissionAsset.FEEDBACK]: PermissionAction.READ };

  const observabilityButtonRef = React.useRef<HTMLButtonElement>(null);

  const columns = [
    {
      title: NAME_LABEL,
      dataIndex: 'name',
      key: 'name',
      width: '40%',
      render: (_: never, row: IPipeline) => (
        <WorkspaceLink to={`/pipelines/${row.name}`} className={styles.nameLink}>
          {row.name}
        </WorkspaceLink>
      ),
    },
    {
      title: SERVICE_LEVEL_LABEL,
      dataIndex: 'service_level',
      key: 'service_level',
      width: '10%',
      render: (_: never, row: IPipeline) => {
        return (
          <>
            <PipelineServiceLevelBadge serviceLevel={row.service_level} />
            <span className={styles.serviceLevelLabel}>
              {
                PIPELINE_SERVICE_LEVELS_LABELS[
                  row.service_level as keyof typeof PIPELINE_SERVICE_LEVELS_LABELS
                ]
              }
            </span>
          </>
        );
      },
    },
    {
      title: DATE_LABEL,
      dataIndex: 'created_at',
      key: 'created_at',
      width: '15%',
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: STATUS_LABEL,
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      render: (_: never, row: IPipeline) => {
        return (
          <div className={styles.pipelineStatus}>
            <PipelineStatusTag pipeline={row} />
            {isPipelineUnhealthy(row) && (
              <Tooltip placement="top" title={getUnhealthyMessage(row)}>
                <WarningOutlined className={styles.pipelineStatus_unhealthyIcon} />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: CREATED_BY_LABEL,
      dataIndex: 'created_by',
      key: 'created_by',
      width: '10%',
      render: (_: never, row: IPipeline) => {
        return (
          <div className={styles.createdBy}>
            <UserAvatar user={row.created_by} />
          </div>
        );
      },
    },
    {
      key: 'action',
      width: '15%',
      align: 'right' as const,
      render: (_: never, record: IPipeline) => {
        const items = [
          {
            label: DETAILS_BUTTON_LABEL,
            key: MenuActionsPipelinesList.Details,
          },
          {
            label: EDIT_BUTTON_LABEL,
            key: MenuActionsPipelinesList.Edit,
          },
          {
            label: RENAME_BUTTON_LABEL,
            key: MenuActionsPipelinesList.RenamePipeline,
            permissions: writePipelinePermission,
          },
          {
            label: DUPLICATE_BUTTON_LABEL,
            key: MenuActionsPipelinesList.Duplicate,
            permissions: writePipelinePermission,
          },
          ...(record.status === PipelineStatusCodes.DEPLOYED &&
          record.service_level !== PipelineServiceLevel.PRODUCTION
            ? [
                {
                  label: DEBUG_BUTTON_LABEL,
                  key: MenuActionsPipelinesList.StartDebugTunnel,
                  permissions: writePipelinePermission,
                },
              ]
            : []),
          ...(record.status === PipelineStatusCodes.DEPLOYED && record.supports_prompt
            ? [
                {
                  label: PROMPT_EXPLORER_BUTTON_LABEL,
                  key: MenuActionsPipelinesList.PromptExplorer,
                },
              ]
            : []),
          ...(record.output_type === PipelineOutputType.GENERATIVE
            ? [
                {
                  label: OBSERVABILITY_BUTTON_LABEL,
                  key: MenuActionsPipelinesList.Observability,
                  permissions: readPipelinePermission,
                },
              ]
            : []),
          {
            label: EXPORT_PIPELINE_FEEDBACK_CSV_BUTTON_LABEL,
            key: MenuActionsPipelinesList.ExportFeedbackCSV,
            permissions: readFeedbackPermission,
          },
          ...(record.desired_status === PipelineDesiredStatusCodes.DEPLOYED
            ? [
                {
                  label: UNDEPLOY_BUTTON_LABEL,
                  key: MenuActionsPipelinesList.Undeploy,
                  'data-testid': 'undeployPipeline_menuActionsButton',
                  permissions: writePipelinePermission,
                },
              ]
            : []),
          {
            label: DELETE_BUTTON_LABEL,
            key: MenuActionsPipelinesList.Delete,
            danger: true,
            icon: <DeleteOutlined />,
            permissions: writePipelinePermission,
          },
        ];

        let buttonProps: WithPermissions<{
          label: string;
          action: (pipelineName: string) => Promise<void>;
          loading: boolean;
          disabled: boolean;
        }> = {
          label: DEPLOY_BUTTON_LABEL,
          action: deployPipeline,
          loading: pipelineInProgress(record.status),
          disabled: isLimitedUser && isLimitExceed(LimitCategory.CREDITS),
          permissions: writePipelinePermission,
        };

        if (record.desired_status === PipelineDesiredStatusCodes.DEPLOYED) {
          if (record.status === PipelineStatusCodes.DEPLOYMENT_FAILED)
            buttonProps = { ...buttonProps, label: TRY_AGAIN_BUTTON_LABEL };
          else if (record.status === PipelineStatusCodes.IDLE)
            buttonProps = {
              ...buttonProps,
              label: ACTIVATE_PIPELINE_BUTTON_LABEL,
              action: activatePipeline,
            };
          else
            buttonProps = {
              ...buttonProps,
              label: SHARE_BUTTON_LABEL,
              action: async (pipelineName: string) => setPipelineToShare(pipelineName),
              permissions: writeSharedPrototypePermission,
            };
        }

        if (pipelineInProgress(record.status))
          buttonProps = {
            ...buttonProps,
            label: CANCEL_BUTTON_LABEL,
            action:
              record.status === PipelineStatusCodes.DEPLOYMENT_IN_PROGRESS ||
              record.status === PipelineStatusCodes.DEPLOYMENT_SCHEDULED
                ? () => onUndeployPipeline(record)
                : deployPipeline,
            loading: false,
            disabled: false,
          };

        return (
          <DataTableActions
            menu={{
              items,
              onClick: ({ key }) => handleMoreActionClick(key, record.name, record),
              'data-testid': 'pipelines_tableRow_moreActions_button',
              ref:
                !observabilityButtonRef.current &&
                record.output_type === PipelineOutputType.GENERATIVE
                  ? observabilityButtonRef
                  : undefined,
            }}
            item={record.name}
            onDelete={deletePipeline}
            onCancelDelete={() => setPipelineToDelete(null)}
            primaryButton={
              record.status !== PipelineStatusCodes.ACTIVATING ? buttonProps : undefined
            }
            cancelButtonLabel={DECLINE_BUTTON_LABEL}
          />
        );
      },
    },
  ];

  return { columns };
};
